import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GraduationCap, Car, Loader2, Snowflake } from "lucide-react";
import { toast } from "sonner";

export default function RoleSelect() {
  const [role, setRole] = useState("student");
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  if (user && user.role) { navigate("/dashboard", { replace: true }); return null; }

  const submit = async () => {
    setLoading(true);
    try {
      const payload = { role };
      if (role === "instructor") payload.invitation_code = inviteCode;
      const { data } = await api.post("/auth/set-role", payload);
      setUser(data);
      toast.success("You're all set!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { key: "student", label: "I'm a Student", desc: "I want to book and take driving lessons.", icon: GraduationCap },
    { key: "instructor", label: "I'm an Instructor", desc: "I want to offer lessons and manage bookings.", icon: Car },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-lg animate-fade-up">
        <div className="flex items-center gap-2 mb-6 text-arctic-secondary">
          <Snowflake className="w-6 h-6 text-arctic-primary" />
          <span className="font-heading font-extrabold text-xl">ArcticDrive</span>
        </div>
        <h1 className="font-heading font-extrabold text-3xl text-arctic-secondary tracking-tight">How will you use Arctic?</h1>
        <p className="mt-2 text-muted-foreground">Choose your role to finish setting up your account.</p>

        <div className="grid gap-4 mt-8">
          {roles.map((r) => (
            <button key={r.key} onClick={() => setRole(r.key)} data-testid={`select-role-${r.key}`}
              className={`flex items-start gap-4 rounded-2xl border p-5 text-left transition-colors duration-200 ${role === r.key ? "border-arctic-primary bg-arctic-primary/5 ring-2 ring-arctic-primary/30" : "border-border hover:border-arctic-primary/50"}`}>
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${role === r.key ? "bg-arctic-primary text-white" : "bg-muted text-muted-foreground"}`}>
                <r.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="font-heading font-semibold text-arctic-secondary">{r.label}</div>
                <div className="text-sm text-muted-foreground">{r.desc}</div>
              </div>
            </button>
          ))}
        </div>

        {role === "instructor" && (
          <div className="mt-4 space-y-2">
            <Label htmlFor="invite">Instructor invitation code</Label>
            <Input id="invite" value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} data-testid="roleselect-invite-code" placeholder="ARCTIC-XXXX-XXXX" className="font-mono uppercase" />
            <p className="text-xs text-muted-foreground">Instructor accounts require an invitation from an administrator.</p>
          </div>
        )}

        <Button onClick={submit} disabled={loading} data-testid="confirm-role" className="w-full h-11 mt-8 rounded-full bg-arctic-primary hover:bg-arctic-primary/90">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
        </Button>
      </div>
    </div>
  );
}
